import { useState, useRef, useEffect } from 'react';
import type { SigningField, SignatureMethod } from '../../../types/esign';

interface Props {
  field: SigningField;
  onSubmit: (data: string, method: SignatureMethod) => void;
  onClose: () => void;
}

export default function SignatureCapture({ field, onSubmit, onClose }: Props) {
  const [method, setMethod] = useState<SignatureMethod>('DRAW');
  const [typedText, setTypedText] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const isDrawing = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });

  // Initialize canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = 400;
    canvas.height = 150;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1a1a1a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [method]);

  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    if ('touches' in e) {
      const touch = e.touches[0];
      return { x: (touch.clientX - rect.left) * scaleX, y: (touch.clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    isDrawing.current = true;
    lastPos.current = getPos(e);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    if (!isDrawing.current) return;
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const pos = getPos(e);

    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };

  const stopDrawing = () => {
    isDrawing.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  const handleSubmit = () => {
    if (method === 'DRAW') {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dataUrl = canvas.toDataURL('image/png');
      onSubmit(dataUrl, 'DRAW');
    } else if (method === 'TYPE') {
      if (!typedText.trim()) return;
      // Generate typed signature as canvas image
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 150;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = 'italic 36px "Brush Script MT", "Segoe Script", cursive';
      ctx.fillStyle = '#1a1a1a';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedText, 20, 75);
      const dataUrl = canvas.toDataURL('image/png');
      onSubmit(dataUrl, 'TYPE');
    }
  };

  const isFieldInitials = field.fieldType === 'INITIALS';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-5 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {isFieldInitials ? 'Add Your Initials' : 'Add Your Signature'}
          </h3>
          <p className="text-sm text-gray-500 mt-1">{field.label}</p>
        </div>

        {/* Method tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setMethod('DRAW')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium ${method === 'DRAW' ? 'text-gray-900 border-b-2 border-gray-900' : 'text-gray-500'}`}
          >
            Draw
          </button>
          <button
            onClick={() => setMethod('TYPE')}
            className={`flex-1 px-4 py-2.5 text-sm font-medium ${method === 'TYPE' ? 'text-gray-900 border-b-2 border-gray-900' : 'text-gray-500'}`}
          >
            Type
          </button>
        </div>

        <div className="p-5">
          {method === 'DRAW' && (
            <div>
              <canvas
                ref={canvasRef}
                className="w-full border border-gray-200 rounded cursor-crosshair touch-none"
                style={{ height: 150 }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              <button onClick={clearCanvas} className="mt-2 text-xs text-gray-500 hover:text-gray-700">
                Clear
              </button>
            </div>
          )}

          {method === 'TYPE' && (
            <div>
              <input
                type="text"
                value={typedText}
                onChange={(e) => setTypedText(e.target.value)}
                placeholder={isFieldInitials ? 'Type your initials' : 'Type your full name'}
                className="w-full px-4 py-3 border border-gray-200 rounded text-gray-900 focus:outline-none focus:border-gray-400"
              />
              {typedText && (
                <div className="mt-3 p-4 bg-gray-50 rounded border">
                  <p className="text-2xl italic text-gray-900" style={{ fontFamily: '"Brush Script MT", "Segoe Script", cursive' }}>
                    {typedText}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-5 border-t bg-gray-50 flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border rounded hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleSubmit} className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gray-900 rounded hover:bg-gray-800">
            {isFieldInitials ? 'Apply Initials' : 'Apply Signature'}
          </button>
        </div>
      </div>
    </div>
  );
}
